import React from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
} from "@mui/material";
import { MAIN_COLOR_RED } from "../assets/styles";
import { useNavigate } from "react-router-dom";
import { SavedShoppingList } from "../redux/shoppingListSlice";
import AddIcon from "@mui/icons-material/Add";

interface SelectShoppingListStepProps {
    savedLists: SavedShoppingList[];
    selectedList: SavedShoppingList | null;
    onSelectList: (list: SavedShoppingList) => void;
}

const SelectShoppingListStep: React.FC<SelectShoppingListStepProps> = ({
    savedLists,
    selectedList,
    onSelectList,
}) => {
    const navigate = useNavigate();

    const getListItemsCount = (list: SavedShoppingList): number => {
        return Object.values(list.items).reduce((sum, qty) => sum + qty, 0);
    };

    // Ensure savedLists is an array (should always be from Redux, but safety check)
    // and filter out any completed lists so they can't be selected for a new route.
    const safeSavedLists = (Array.isArray(savedLists) ? savedLists : []).filter(
        (list) => !list.is_completed
    );

    return (
        <Box>
            <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: MAIN_COLOR_RED, mb: 1 }}
            >
                Shopping List
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Select your shopping list or create a new one
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => navigate("/lists")}
                    sx={{
                        backgroundColor: MAIN_COLOR_RED,
                        color: "white",
                        width: 32,
                        height: 32,
                        "&:hover": {
                            backgroundColor: MAIN_COLOR_RED,
                        },
                    }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Box>

            {safeSavedLists.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        No shopping lists found. Create one first!
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/lists")}
                        sx={{ backgroundColor: MAIN_COLOR_RED }}
                    >
                        Create List
                    </Button>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {safeSavedLists.map((list) => (
                        <Grid item xs={12} key={list.id}>
                            <Card
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    "&:hover": {
                                        boxShadow: 4,
                                    },
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Chip
                                                label={`${getListItemsCount(list)} items`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: MAIN_COLOR_RED,
                                                    color: "white",
                                                    mb: 1,
                                                    fontWeight: "bold",
                                                }}
                                            />
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                {list.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Description
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant={selectedList?.id === list.id ? "contained" : "outlined"}
                                            onClick={() => onSelectList(list)}
                                            sx={{
                                                backgroundColor: selectedList?.id === list.id ? MAIN_COLOR_RED : "transparent",
                                                color: selectedList?.id === list.id ? "white" : MAIN_COLOR_RED,
                                                borderColor: MAIN_COLOR_RED,
                                                minWidth: 100,
                                                "&:hover": {
                                                    backgroundColor: selectedList?.id === list.id ? MAIN_COLOR_RED : "rgba(220, 38, 38, 0.1)",
                                                },
                                            }}
                                        >
                                            {selectedList?.id === list.id ? "Selected" : "Select"}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default SelectShoppingListStep;

